/**
 * app/screens/SignUpScreen.tsx
 * -----------------------------
 * What: Account creation screen for new users.
 * Why: Captures minimal information to get started and integrates with Supabase auth.
 */
import React, { useState } from 'react';
import { View, TouchableOpacity, Text, StyleSheet, SafeAreaView, StatusBar, Modal } from 'react-native';
import { useAuth } from '../../lib/AuthContext';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import CustomAlert from '../components/CustomAlert';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../lib/ThemeContext';
// import * as WebBrowser from 'expo-web-browser';
import DocumentViewerModal from '../components/DocumentViewerModal';
import ThemedButton from '../components/ThemedButton';
import FormTextInput from '../components/FormTextInput';

type RootStackParamList = {
  Login: undefined;
  SignUp: undefined;
  Home: undefined;
};

type Props = NativeStackScreenProps<RootStackParamList, 'SignUp'>;

export default function SignUpScreen({ navigation }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { signUp } = useAuth();
  const { theme } = useTheme();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [openDoc, setOpenDoc] = useState<string | null>(null);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptGdprSpecial, setAcceptGdprSpecial] = useState(false);
  const allAccepted = acceptTerms && acceptPrivacy && acceptGdprSpecial;

  const handleSignUp = async () => {
    console.log('SignUp: Attempting sign up with', email);
    if (!email || !password || !confirmPassword) {
      setInfoModalMessage('Please complete all fields');
      setShowInfoModal(true);
      return;
    }

    if (password !== confirmPassword) {
      setInfoModalMessage('Passwords do not match');
      setShowInfoModal(true);
      return;
    }

    if (!allAccepted) {
      setInfoModalMessage('You must accept all Terms & Policies to create an account.');
      setShowInfoModal(true);
      return;
    }

    try {
      setIsLoading(true);
      const response = await signUp(email, password);
      console.log('SignUp: Supabase response:', JSON.stringify(response, null, 2));
      
      if (response.error) {
        console.log('SignUp: Detailed error:', JSON.stringify(response.error, null, 2));
        let errorMessage = response.error.message;
        
        // Provide more specific error messages
        if (response.error.status === 500) {
          errorMessage = 'Unable to complete signup. This might be because:\n\n1. The email service is not configured\n2. There are temporary server issues\n\nPlease try again later or contact support.';
        } else if (response.error.message.includes('429') || response.error.status === 429) {
          errorMessage = 'Too many signup attempts. Please wait for an hour before trying again.';
        } else if (response.error.message.includes('email')) {
          errorMessage = 'Invalid email format or email already in use';
        } else if (response.error.message.includes('password')) {
          errorMessage = 'Password must be at least 6 characters long';
        } else if (response.error.message.includes('smtp') || response.error.message.includes('email service')) {
          errorMessage = 'Error with email service. Please try again later or contact support.';
        }
        
        throw new Error(errorMessage);
      }
      
      setShowSuccessModal(true);
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.log('SignUp: Caught error:', error);
      setInfoModalMessage(error.message || 'An error occurred during sign up');
      setShowInfoModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.background }]}> 
      <StatusBar barStyle="light-content" backgroundColor={theme.background} />
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={[styles.header, { borderBottomColor: theme.border }]}>
          <Text style={[styles.title, { color: theme.text }]}>Log My Home</Text>
        </View>

        <View style={styles.content}>
          <Text style={[styles.welcomeText, { color: theme.text }]}>Create Account</Text>
          <Text style={[styles.subtitle, { color: theme.text }]}>Sign up to get started</Text>

          <View style={styles.form}>
            <FormTextInput
              style={[styles.input, { backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border }]}
              placeholder="Email"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              accessibilityLabel="Email input"
              accessibilityHint="Enter your email address"
              placeholderTextColor={theme.secondaryText}
            />
            <View style={styles.passwordContainer}>
              <FormTextInput
                style={[styles.input, styles.passwordInput, { backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border }]}
                placeholder="Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                accessibilityLabel="Password input"
                accessibilityHint="Enter your password"
                placeholderTextColor={theme.secondaryText}
              />
              <TouchableOpacity
                style={[styles.eyeButton, { backgroundColor: theme.inputBackground }]}
                onPress={() => setShowPassword(!showPassword)}
                accessibilityLabel={showPassword ? "Hide password" : "Show password"}
              >
                <Ionicons
                  name={showPassword ? "eye-off" : "eye"}
                  size={24}
                  color={theme.text}
                />
              </TouchableOpacity>
            </View>
            <View style={styles.passwordContainer}>
              <FormTextInput
                style={[styles.input, styles.passwordInput, { backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border }]}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                secureTextEntry={!showConfirmPassword}
                accessibilityLabel="Confirm password input"
                accessibilityHint="Confirm your password"
                placeholderTextColor={theme.secondaryText}
              />
              <TouchableOpacity
                style={[styles.eyeButton, { backgroundColor: theme.inputBackground }]}
                onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                accessibilityLabel={showConfirmPassword ? "Hide password" : "Show password"}
              >
                <Ionicons
                  name={showConfirmPassword ? "eye-off" : "eye"}
                  size={24}
                  color={theme.text}
                />
              </TouchableOpacity>
            </View>

            <Text style={[styles.acceptancePrompt, { color: theme.text }]}>By ticking each box below you confirm that you have read and accept the following documents:</Text>

            <View style={{ marginTop: 16 }}>
              {[{
                label: 'Terms & Conditions',
                state: acceptTerms,
                setter: setAcceptTerms,
                url: 'https://legal.logmyhome.com/terms/v1.0.0/terms.html'
              }, {
                label: 'Privacy Policy',
                state: acceptPrivacy,
                setter: setAcceptPrivacy,
                url: 'https://legal.logmyhome.com/privacy/v1.0.0/privacy.html'
              }, {
                label: 'GDPR Special Category Data Statement',
                state: acceptGdprSpecial,
                setter: setAcceptGdprSpecial,
                url: 'https://legal.logmyhome.com/gdpr-special/v1.0.0/gdpr-special.html'
              }].map((item) => (
                <TouchableOpacity
                  key={item.label}
                  style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}
                  onPress={() => item.setter(!item.state)}
                >
                  <Ionicons name={item.state ? 'checkbox' : 'square-outline'} size={22} color={theme.accent} />
                  <Text style={{ color: theme.text, marginLeft: 8, flex: 1 }}>{item.label}</Text>
                  <TouchableOpacity onPress={() => setOpenDoc(item.url)}>
                    <Ionicons name="open-outline" size={18} color={theme.accent} />
                  </TouchableOpacity>
                </TouchableOpacity>
              ))}
            </View>

            <ThemedButton
              title={isLoading ? 'Signing Up...' : 'Sign Up'}
              onPress={handleSignUp}
              disabled={!allAccepted || isLoading}
              accessibilityLabel={isLoading ? 'Signing up' : 'Sign up'}
              style={{ marginTop: 24 }}
            />

            <TouchableOpacity 
              style={[styles.linkButton, { backgroundColor: theme.background }]}
              onPress={() => navigation.navigate('Login')}
              accessibilityRole="link"
              accessibilityLabel="Already have an account? Sign in"
            >
              <Text style={[styles.linkText, { color: theme.text }]}>Already have an account? Sign in</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Info/Error Modal */}
        <Modal
          visible={showInfoModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowInfoModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
            <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
              <Text style={[styles.modalTitle, { color: theme.text }]}>Error</Text>
              <Text style={[styles.modalMessage, { color: theme.text }]}>{infoModalMessage}</Text>
              <ThemedButton title="OK" onPress={() => setShowInfoModal(false)} style={{ minWidth: 140, alignSelf: 'center' }} />
            </View>
          </View>
        </Modal>

        {/* Success Modal */}
        <Modal
          visible={showSuccessModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowSuccessModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
            <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
              <Text style={[styles.modalTitle, { color: theme.text }]}>Sign Up Successful</Text>
              <Text style={[styles.modalMessage, { color: theme.text }]}>
                Please check your email to verify your account. If you don't receive an email within a few minutes, please check your spam folder.
              </Text>
              <ThemedButton
                title="OK"
                onPress={() => {
                  setShowSuccessModal(false);
                  // Navigate to Login screen after successful sign-up
                  navigation.navigate('Login');
                }}
                style={{ minWidth: 140, alignSelf: 'center' }}
              />
            </View>
          </View>
        </Modal>
      </View>
    </SafeAreaView>
    {/* In-app document viewer */}
    <DocumentViewerModal visible={!!openDoc} url={openDoc} onClose={() => setOpenDoc(null)} />
    </>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  welcomeText: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 30,
    textAlign: 'center',
  },
  form: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  input: {
    borderWidth: 1,
    borderColor: '#333',
    padding: 15,
    borderRadius: 6,
    marginBottom: 15,
    fontSize: 16,
  },
  button: {
    padding: 15,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 10,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  linkButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  linkText: {
    fontSize: 16,
  },
  acceptancePrompt: {
    fontSize: 14,
    marginTop: 12,
    marginBottom: 4,
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  passwordInput: {
    flex: 1,
    marginBottom: 0,
  },
  eyeButton: {
    position: 'absolute',
    right: 15,
    padding: 5,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#232323',
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    marginBottom: 20,
    textAlign: 'center',
  },
  modalButton: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
    marginTop: 8,
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  sendButton: {
    backgroundColor: '#007bff',
  },
}); 